import { Sprite, Texture, Rectangle } from 'pixi.js';
import { toast, notifyOnce } from '../../dom';
import { playSfx } from '../../audio/audio';
import { faunaApi, fieldApi, woodcuttingApi, miningApi, gatheringApi, horseApi,
  type ChopResult, type MineResult } from '../../game-api';
import { ApiError } from '../../../core/network/api';
import { refreshGraves } from './graves';
import { dispatchAction, isDropped } from '../../action/dispatch';
import { TILE, treeSizeOf, saplingDecorId, SAPLING_VARIANT, type Decor } from '../../map/world';
import { TREE_KINDS } from '../../map/textures';
import { oreByVariant } from '../../map/ores';
import { FOOT_OFFSET } from '../../map/player';
import { saveHorseState } from '../../map/horse-state';
import {
  BOULDER_PASSES, CUT_SECONDS, GATHER_SECONDS, MINE_SECONDS, PX, bkey, gatherResource, guard, isCleared,
  isGathered, isMined, isStump, itemTexture, applyItemTexture, stumpFor, toolAction, type FarmCtx, refreshBag,} from './context';
import { buildCollision, fmtWait, walkTo } from './scene';
import { nearObject, unreachableSay } from './input';
import { huntDamage, swingDamage } from '../../combat/damage';
import { AUTO_CADENCE_MS, ENGAGE_LEASH, isHostileState, selectAutoTarget } from '../../combat/tuning';

/**
 * Fallback respawn windows, used only when the server's config has not arrived.
 *
 * These MUST track the backend constants (`woodcutting.config.ts`,
 * `mining.config.ts`, `gathering.config.ts`) — they are the same numbers, not an
 * independent client-side balance choice. They were left at the old three-to-five
 * minutes when the server moved to hours, which meant a dropped config quietly gave
 * back the exact bug the longer windows were meant to fix: a tree you had just
 * felled standing again by the time you walked back past it, while the server still
 * considered it cut for the rest of the day.
 */
const H = 60 * 60_000;
const FALLBACK_REGROW = {
  tree: 8 * H,
  ore: 12 * H,
  stone: 8 * H,
  bush: 6 * H,
  mushroom: 4 * H,
  foliage: 2 * H,
} as const;

// ─── Energy pre-check: refuse BEFORE the work, not after ─────────────────────
/**
 * Every energy cost used to be discovered on COMPLETION: a player with an empty
 * bar started a chop, watched the whole progress ring fill, and only then got the
 * server's refusal. The cost is known up front, so the refusal belongs up front.
 *
 * The costs are NOT re-declared here — they come from the server (the woodcutting
 * / mining / gathering status endpoints, and the field snapshot's `energyCosts`
 * mirror where the server publishes it). When a cost is unknown (an older server,
 * or a config that has not arrived yet) the client must NOT guess: it lets the
 * action through and the server decides exactly as it does today.
 *
 * Movement is deliberately never gated.
 */
export type EnergyAction = 'chop' | 'mine' | 'gather:stone' | 'gather:mushroom' | 'gather:bush'
  | 'foliage' | 'till' | 'plant' | 'water' | 'harvest' | 'swing' | 'cast';

export type EnergyCheck = { ok: true } | { ok: false; need: number; have: number };

/**
 * Pure predicate. `cost === undefined` means "the client does not know this cost"
 * → allowed. Exactly enough energy is enough (an epsilon absorbs the float noise
 * of fractional costs like water's 0.3 and swing's 0.5).
 */
export function checkEnergy(cost: number | null | undefined, energy: number | null | undefined): EnergyCheck {
  if (cost == null || !Number.isFinite(cost) || cost <= 0) return { ok: true };
  if (energy == null || !Number.isFinite(energy)) return { ok: true };
  if (energy + 1e-9 >= cost) return { ok: true };
  return { ok: false, need: cost, have: Math.max(0, energy) };
}

/** What the server says this action costs, or undefined when it hasn't said. */
export function energyCostOf(c: FarmCtx, action: EnergyAction): number | undefined {
  const field = c.farm?.energyCosts;
  switch (action) {
    case 'chop': return c.woodCfg?.energyCostChop;
    case 'mine': return c.mineCfg?.energyCostMine;
    case 'gather:stone': return c.gatherCfg?.energyCostStone;
    // A bush is picked like a mushroom — same gathering cost on the server.
    case 'gather:mushroom':
    case 'gather:bush': return c.gatherCfg?.energyCostMushroom;
    case 'foliage': return c.gatherCfg?.energyCostFoliage;
    // Fishing's cost rides on its own status payload (fishCfg), so a Vigor draught
    // discounts a cast exactly as it discounts a swing.
    case 'cast': return c.fishCfg?.energyCostCast;
    default: return field?.[action];
  }
}

const fmtE = (n: number) => (Math.round(n * 10) / 10).toString();

/**
 * Gate an action on energy. Returns false (and says why, once) when the player
 * cannot afford it. "Once per state" is the point: a player tapping a tree ten
 * times must not get ten toasts, but must hear it again after resting — passing
 * `null` on success clears the latch (see dom.ts notifyOnce).
 *
 * Energy is read from the LIVE farm payload, so passive regen / a changed rest
 * value are picked up without this module knowing anything about them.
 */
export function affordEnergy(c: FarmCtx, action: EnergyAction): boolean {
  const r = checkEnergy(energyCostOf(c, action), c.farm?.energy);
  const key = `energy:${action}`;
  if (r.ok) { notifyOnce(key, null); return true; }
  notifyOnce(key, `Not enough energy — needs ${fmtE(r.need)}, you have ${fmtE(r.have)}. Rest to recover.`, 'error');
  return false;
}

// Face a target by its DOMINANT axis (4-way): a mob to the north makes the hero
// swing up, not sideways. Previously attacks always chose left/right from the x
// delta alone, so attacking straight up/down snapped the hero sideways then back.
function faceDir(dx: number, dy: number): 'up' | 'down' | 'left' | 'right' {
  if (Math.abs(dx) >= Math.abs(dy)) return dx < 0 ? 'left' : 'right';
  return dy < 0 ? 'up' : 'down';
}

/**
 * Swing at an animal. Only lands if the hero is within reach — otherwise the
 * click is ignored rather than teleporting damage across the map.
 *
 * The kill is reported to the server AFTER the client's HP hits zero. The
 * server rolls the drop; we only name the species. It cannot verify the kill
 * (animals are client-side, like movement) but it decides every quantity, so
 * the worst a tampered client does is claim kills, not mint leather.
 */
const ATTACK_REACH = 1.6 * TILE;

/**
 * Try to tame the wild horse the player clicked. Feeds every carrot in the bag to
 * raise the odds — the server spends what it finds and rolls the result. On success
 * the horse becomes yours and is placed via the riding layer.
 */
async function tameHorse(c: FarmCtx, variant: string) {
  // A refusal that is true for as long as you own a horse — say it once.
  if (c.horseStatus?.horse) { notifyOnce('tame', 'You already have a horse'); return; }
  const carrots = c.bagCache?.items.find((i) => i.itemId === 'produce_carrot')?.quantity ?? 0;
  const feeds = Math.min(carrots, 4);
  try {
    const r = await dispatchAction('horse.tame', (id) => horseApi.tame(variant, feeds, id), { key: variant });
    if (r.success && r.type) {
      const info = c.horseStatus?.types.find((t) => t.id === r.type)
        ?? { id: r.type as 'work' | 'racing' | 'heavy', speed: 7, bagSlots: 0, fearless: false };
      c.horseStatus = { ...(c.horseStatus ?? { types: [], tame: { baseChance: 0.4, perFeed: 0.15, maxChance: 1, feedItems: [] }, horse: null }), horse: r.type as 'work' | 'racing' | 'heavy' };
      // Place it at the hero's FEET tile (player.y is the sprite anchor, a tile
      // above the feet — using it dropped the horse a tile north of the player).
      if (c.player) {
        const htx = Math.floor(c.player.x / TILE), hty = Math.floor((c.player.y - FOOT_OFFSET) / TILE);
        c.riding?.setHorse(info.id, info, htx, hty);
        // Remember it from the moment it is tamed, not from the first tab switch —
        // otherwise a player who tames and immediately opens the Shop comes back to
        // a horse standing beside the plots (map/horse-state.ts).
        saveHorseState(c.seed, htx, hty, false);
      }
      toast(`Tamed a ${r.type} horse! 🐴 Press E to ride`);
    } else {
      toast(`The horse bolted (${Math.round(r.chance * 100)}% · ${r.feedsUsed} fed)`, 'error');
    }
    await c.game.refreshHud();
      refreshBag(c);
  } catch (e) {
    toast(e instanceof ApiError ? e.message : 'Taming failed', 'error');
  }
}

/**
 * Swing at a night mob. Its death is reported by the mobs layer's onDeath callback
 * (which POSTs the kill and drops the loot), so this only lands the hit and the
 * swing animation.
 */
export function attackMob(c: FarmCtx, wx: number, wy: number, lock = true): boolean {
  const { mobs, player } = c;
  if (!mobs || !player) return false;
  const near = mobs.entities.find((m) => m.state !== 'dying' && m.state !== 'spawning'
    && Math.abs(wx - m.x) < 34 && wy - m.y < 12 && wy - m.y > -70);   // §2.1: widened for larger mobs
  if (!near) return false;
  // Lock onto this mob only when the PLAYER chose the fight (a click). Auto-attack
  // passes lock=false: it used to set the lock as a side effect, which turned a
  // reflex swing at something wandering past into a committed pursuit the player
  // never asked for — the core of the "too aggressive" complaint.
  if (lock) c.combatTargetId = near.id;
  const reach = Math.hypot(near.x - player.x, near.y - (player.y - FOOT_OFFSET));
  if (reach > ATTACK_REACH) { walkTo(c, Math.floor(near.x / TILE), Math.floor(near.y / TILE)); return true; }
  // Walking into reach is movement (never gated); the SWING is the paid part.
  if (!affordEnergy(c, 'swing')) return true;
  const dir = faceDir(near.x - player.x, near.y - (player.y - FOOT_OFFSET));
  void player.playAction('attack', dir);
  // ONE sound per attack. This used to fire the swing AND, a moment later, a
  // separate "connected" cue — two sounds for one action, on top of whatever the
  // mob itself was making. Layered over auto-attack's cadence and several mobs at
  // once it stopped reading as a sword and started reading as noise. One swing,
  // one sound; the hit is communicated by the damage number and the slash effect.
  playSfx('sword_swing', { cooldownMs: 260, maxConcurrent: 1 });
  const dmg = swingDamage(c.gearTiers.sword);
  const res = mobs.hitAt(near.x, near.y, dmg);
  if (res && c.effects) {   // §7: visible slash + hit spark + damage number on connect
    c.effects.slash(near.x, near.y, dir, 0xfff0a0);
    c.effects.hit(near.x, near.y - TILE * 0.4, dmg, 0xfff0a0);
  }
  return true;
}

export function attackAnimal(c: FarmCtx, wx: number, wy: number): boolean {
  const { fauna, player } = c;
  if (!fauna || !player) return false;
  const hit = fauna.entities.find((e) => e.state !== 'dead'
    && Math.abs(wx - e.x) < 34 && wy - e.y < 12 && wy - e.y > -70);   // §2.3: widened for larger animals
  if (!hit) return false;
  const st = c.faunaStats[hit.a.species];
  if (st && !st.killable) {
    // A horse is tamed, not fought. Walk over, then attempt it — feeding it any
    // carrots in the bag first to raise the odds (the server counts what's there).
    if (hit.a.species === 'horse') { void tameHorse(c, hit.a.variant); return true; }
    // Keyed by species: swapping targets is new information, re-clicking the same
    // swan is not.
    notifyOnce(`hunt:${hit.a.species}`, `${st.name}s can't be hunted`);
    return true;
  }
  const reach = Math.hypot(hit.x - player.x, hit.y - (player.y - FOOT_OFFSET));
  if (reach > ATTACK_REACH) { walkTo(c, Math.floor(hit.x / TILE), Math.floor(hit.y / TILE)); return true; }

  if (!affordEnergy(c, 'swing')) return true;
  const adir = faceDir(hit.x - player.x, hit.y - (player.y - FOOT_OFFSET));
  void player.playAction('attack', adir);
  playSfx('sword_swing', { cooldownMs: 260, maxConcurrent: 1 });
  const hdmg = huntDamage(c.gearTiers.sword);
  const res = fauna.hitAt(hit.x, hit.y, hdmg);
  if (c.effects) { c.effects.slash(hit.x, hit.y, adir, 0xfff0a0); c.effects.hit(hit.x, hit.y - TILE * 0.3, hdmg, 0xfff0a0); }
  if (!res) return true;
  // A swan fights back.
  const rd = c.faunaStats[res.entity.a.species]?.retaliateDamage;
  if (rd && !res.died) {
    void dispatchAction('field.damage', (id) => fieldApi.damage(rd, id), { noDebounce: true })
      .then((h) => { if (h.dead) { toast('The swan finished you off', 'error'); refreshGraves(); } void c.game.refreshHud();
      refreshBag(c); })
      .catch(() => undefined);
  }
  if (res.died) {
    const e = res.entity;
    void guard('fauna.kill', async (actionId) => {
      const tx = Math.floor(e.x / TILE), ty = Math.floor(e.y / TILE);
      const r = await faunaApi.kill(e.a.species, e.a.variant, { tx, ty }, actionId);
      if (r.leveledUp) playSfx('level_up');
      for (const d of r.dropped) spawnFallingIcons(c, e.x, e.y - 12, 'loot', d.quantity, d.itemId);
      c.onDrops?.(r.dropped);
      toast(`${r.dropped.map((d) => `${d.quantity} ${d.name}`).join(' · ')} dropped${r.leveledUp ? ' · LEVEL UP!' : ''}`);
      await c.game.refreshHud();
      refreshBag(c);
    }, e.a.id);
  }
  return true;
}

// ─── Auto-attack (sword only, field only) ────────────────────────────────────
// Terraboma's controls differ from a classic click-to-attack RPG, so when the
// sword (knife) is equipped the hero DEFENDS itself automatically. It is no longer
// a proximity aggro: see combat/tuning.ts — only mobs already hunting the player
// qualify, only within a short AUTO_RANGE, on a slow cadence, and the swing does
// NOT take the click-to-engage lock. Starting a fight is a click. The Arena is a
// separate view and stays manual click — this only runs on the farm.
let autoCd = 0;
export function autoCombatTick(c: FarmCtx, dtMs: number) {
  if (!c.autoAttack || c.selectedTool !== 'knife' || !c.player) return;
  autoCd -= dtMs;
  if (autoCd > 0) return;
  const px = c.player.x, py = c.player.y - FOOT_OFFSET;
  const mobs = (c.mobs?.entities ?? []).map((m) => ({ x: m.x, y: m.y, state: m.state, hostile: isHostileState(m.state) }));
  const animals = (c.fauna?.entities ?? []).map((e) => ({
    x: e.x, y: e.y, state: e.state, killable: c.faunaStats[e.a.species]?.killable !== false,
  }));
  const pick = selectAutoTarget(mobs, animals, px, py);
  if (!pick) return;
  // lock=false: a defensive swing must never start a pursuit (see attackMob).
  if (pick.kind === 'mob') attackMob(c, pick.x, pick.y, false);
  else attackAnimal(c, pick.x, pick.y);
  autoCd = AUTO_CADENCE_MS;
}

// ─── Click-to-engage: attack a clicked mob until it dies ──────────────────────
// Clicking a mob locks onto it (attackMob sets combatTargetId). Each frame the hero
// pursues + swings at that specific target on a cadence until it's dead or it slips
// past the leash (now a short ENGAGE_LEASH) — then the lock clears and, crucially,
// nothing re-acquires. Killing one mob used to leave auto-attack free to grab the
// next body in a 7-tile radius, so a single click could roll into a whole night.
let targetCd = 0;
export function combatTargetTick(c: FarmCtx, dtMs: number) {
  if (!c.combatTargetId || !c.mobs || !c.player) return;
  const m = c.mobs.entities.find((e) => e.id === c.combatTargetId);
  // Target gone (killed + despawned, dying, or dawn-dissolved) → release the lock.
  if (!m || m.state === 'dying' || m.state === 'spawning') { c.combatTargetId = null; return; }
  const px = c.player.x, py = c.player.y - FOOT_OFFSET;
  if (Math.hypot(m.x - px, m.y - py) > ENGAGE_LEASH) { c.combatTargetId = null; return; }
  targetCd -= dtMs;
  if (targetCd > 0) return;
  targetCd = AUTO_CADENCE_MS;
  attackMob(c, m.x, m.y);   // walks into reach if needed, else swings
}

// ─── Woodcutting: hold to cut (red→green ring), tree falls to a matching stump
// and drops logs you click to pick up ────────────────────────────────────────
export function spawnChips(c: FarmCtx, d: Decor, sprite: Sprite) {
  const { fxLayer, tex } = c;
  if (!fxLayer || !tex) return;
  const rock = d.kind === 'ore_rock' || d.kind === 'rock';
  const species = TREE_KINDS[d.variant >> 1] ?? 'oak';
  const leaf = species.startsWith('spruce') ? tex.fx.needle : species.startsWith('birch') ? tex.fx.leaf_gold : tex.fx.leaf_green;
  const cx = sprite.x, cy = sprite.y - sprite.height * 0.4;
  // OWNER (2026-08-16): "for each tree during cutting, Spruce_Needle_Particle —
  // for each tree needed to be personalized." The art was already per species
  // (oak leaf / birch leaf / spruce needle, sliced from the pack's *_Particle
  // sheets), but you could not tell: four particles a hit, half of them plain
  // brown chips, left roughly TWO leaves on screen for a fraction of a second.
  // Six particles now, and two thirds of them foliage, so the tree you are cutting
  // is legible from the debris. Rocks are unchanged — they have no foliage.
  const count = rock ? 4 : 6;
  for (let i = 0; i < count; i++) {
    // Rocks throw grey stone shards; trees throw brown chips + leaves.
    const chip = rock || Math.random() < 0.34;
    const s = new Sprite(chip ? Texture.WHITE : leaf);
    s.anchor.set(0.5);
    if (chip) { s.tint = rock ? 0x8a8f99 : 0x7a4a24; s.width = rock ? 4 : 5; s.height = 4; } else s.scale.set(PX * 0.7);
    s.x = cx + (Math.random() - 0.5) * TILE * 0.4;
    s.y = cy + (Math.random() - 0.5) * TILE * 0.4;
    fxLayer.addChild(s);
    c.particles.push({ s, vx: (Math.random() - 0.5) * 150, vy: -70 - Math.random() * 80, life: 0, max: 0.45 + Math.random() * 0.3, spin: (Math.random() - 0.5) * 12 });
  }
}

// Facing for a chop: turn toward the tree but NEVER face 'up' — that shows the
// hero's back and reads as "chopping from behind the tree". A tree above the
// hero is chopped in side profile (axe visible); one below is a front swing.
const dirTo = (fx: number, fy: number, tx: number, ty: number): 'down' | 'up' | 'left' | 'right' => {
  const dx = tx - fx, dy = ty - fy;
  if (Math.abs(dx) >= Math.abs(dy)) return dx < 0 ? 'left' : 'right';
  return dy > 0 ? 'down' : (dx <= 0 ? 'left' : 'right');
};

/**
 * Resolve a queued chop/mine once the hero has stopped walking.
 *
 * The drop branch used to be a bare `c.pendingAction = null`: the player tapped a
 * tree, watched the hero walk, and then nothing happened and nothing was said —
 * the straight-line approach had been clamped by a collision a pixel short of the
 * node. That is the owner's "some resources cannot be destroyed" arriving by a
 * second route, and silence is what made it unreadable. `notifyOnce`, keyed per
 * node, says it once rather than once per frame, and `requestOperate` clears the
 * same key when the walk is re-queued so the next honest attempt can speak again.
 */
export function settlePendingAction(c: FarmCtx) {
  const pa = c.pendingAction;
  if (!pa) return;
  if (nearObject(c, pa.d)) { c.pendingAction = null; startCut(c, pa.d, pa.s); return; }
  if (c.player?.moving) return;                    // still walking — give him time
  c.pendingAction = null;
  notifyOnce(`unreachable:${pa.d.id}`, unreachableSay(pa.d.kind), 'error');
}

export function startCut(c: FarmCtx, d: Decor, s: Sprite) {
  // Mushrooms are picked instantly; small stones are MINED (short pass below).
  if (d.kind === 'mushrooms') { if (!isGathered(c, d) && affordEnergy(c, 'gather:mushroom')) void gatherProp(c, d, s); return; }
  // Shared hold-to-gather for trees (axe), ore rocks (pickaxe), stones and bushes.
  // §4: a bush is CHOPPED (axe swing) but finishes via gatherProp like a stone —
  // so gather=true (→ gatherProp) but rock=false (→ 'chop' action, not 'mine').
  // `logs` joins the hold-to-gather props: a woodpile is hauled away with the same
  // swing a bush is chopped with, and pays wood like one (a whole trunk, so more).
  const gather = d.kind === 'rock' || d.kind === 'bush' || d.kind === 'boulder' || d.kind === 'logs';
  // A boulder swings the pickaxe like an ore vein, but resolves through
  // mineBoulderPass (several rounds) rather than breaking open in one.
  const rock = d.kind === 'ore_rock' || d.kind === 'rock' || d.kind === 'boulder';
  if ((gather ? isGathered(c, d) : rock ? isMined(c, d) : isCleared(c, d)) || (c.cutting && c.cutting.id === d.id)) return;
  // Refuse BEFORE the ring starts filling — the whole point of the pre-check.
  // A boulder swings the pickaxe but pays out as a stone gather, so it is priced
  // as one; an ore vein is priced as a mine, everything else as a chop.
  const costKind: EnergyAction = d.kind === 'ore_rock' ? 'mine'
    : d.kind === 'rock' || d.kind === 'boulder' ? 'gather:stone'
    : d.kind === 'bush' || d.kind === 'logs' ? 'gather:bush' : 'chop';
  if (!affordEnergy(c, costKind)) return;
  cancelCut(c);
  const ring = new Sprite(c.tex!.ring[0]);            // red→green radial progress bar
  ring.anchor.set(0.5); ring.scale.set(PX * 0.85);  // a bit smaller
  // Float it above the hero's head (not the tree/rock).
  ring.x = c.player ? c.player.x : s.x;
  ring.y = (c.player ? c.player.y : s.y) - TILE * 3.1;
  c.fxLayer?.addChild(ring);
  const stump = !rock && isStump(c, d);                 // grubbing a stump is quicker than felling
  const dur = gather ? GATHER_SECONDS : rock ? MINE_SECONDS : stump ? CUT_SECONDS * 0.6 : CUT_SECONDS;
  c.cutting = { id: d.id, d, sprite: s, t: 0, ring, chipT: 0, stump, rock, gather, dur };
  // THE WORK decides the animation, not the hotbar.
  //
  // OWNER (2026-08-17): "again, I've changed animation for mining, but you still
  // using a hoe." This read `toolAction(c, rock ? 'mine' : 'chop')`, and
  // toolAction returns the SELECTED tool's action, falling back to the node's only
  // when nothing is selected. So mining a rock with the hoe in hand played the
  // TILLING animation — the swing said one thing and the action did another.
  //
  // autoSelectFor equips the right tool on tap, which is why this mostly looked
  // fine; it is a no-op for a player who deliberately holds something else, and
  // for any node it has no ideal tool for. Keying off the node instead means the
  // hero swings a pickaxe at rock whatever the hotbar says, which is the only
  // version that cannot lie.
  //
  // Gathers (bush, mushroom, ground cover) genuinely have no tool of their own —
  // they come up with whatever is in hand — so they keep the in-hand action.
  const workAction = rock ? 'mine' : gather ? toolAction(c, 'attack') : 'chop';
  if (c.player) c.player.startAction(workAction, dirTo(c.player.x, c.player.y, s.x, s.y));
}

export function cancelCut(c: FarmCtx, id?: string) {
  if (!c.cutting || (id && c.cutting.id !== id)) return;
  c.cutting.ring.destroy();
  c.cutting = null;
  c.player?.stopAction();
}

export function spawnFallingIcons(c: FarmCtx, x: number, y: number, kind: 'wood' | 'ore' | 'gem' | 'stone' | 'mushroom' | 'loot', n: number, itemId?: string) {
  const { fxLayer, tex } = c;
  if (!fxLayer || !tex) return;
  const count = Math.max(1, Math.min(n || 1, 8));
  for (let i = 0; i < count; i++) {
    // Dedicated resource ITEM icons from the pack's Resources sheet — not the
    // map decor sprites (a dropped stone is a pile of chunks, not the rock).
    const t = kind === 'loot' && itemId ? itemTexture(c, itemId)
      : kind === 'mushroom' ? tex.decor.mushrooms[0]
      : tex.res[kind as 'wood' | 'ore' | 'gem' | 'stone'];
    const s = new Sprite(t);                 // real in-game pack sprite, not an emoji
    // Mob/animal loot is not in the preloaded set, so its icon may still be in
    // flight — swap it in when it lands rather than arcing an invisible sprite to
    // the ground (that was the "bones and meat don't appear" report).
    if (kind === 'loot' && itemId) applyItemTexture(c, s, itemId);
    s.anchor.set(0.5); s.scale.set(PX * 0.8);
    s.x = x + (Math.random() - 0.5) * TILE * 0.5;
    s.y = y;
    s.zIndex = 1e6;
    fxLayer.addChild(s);
    c.particles.push({ s, vx: (Math.random() - 0.5) * 110, vy: -160 - Math.random() * 70, life: 0, max: 1 + Math.random() * 0.35, spin: (Math.random() - 0.5) * 2.5 });
  }
}

/**
 * The tutorial glade's stand-ins for a server answer.
 *
 * Phase 1 of the tutorial draws its own 20x50 map with one tree and one rock on
 * it, but those props were chopped and mined through the real API — at glade tile
 * coordinates, against the player's real farm. Mining the training rock therefore
 * wrote a permanent `ore_rock` harvest row at (7,10), which retired the real
 * world's tile (7,10) sight unseen and, worse, made the tutorial unfinishable the
 * second time it ran: the row was still there, so every click on the training
 * rock came back `400 That vein is worked out - find another`.
 *
 * A training prop is not a resource. In the glade the swing plays, the tree falls
 * and the rock cracks, and nothing is spent, granted or persisted — which is also
 * what `sandbox-tour.ts` already assumed ("the server action may not fire").
 */
const SANDBOX_CHOP: ChopResult = {
  logsDropped: 0, drops: [], energy: 0, maxEnergy: 0,
  xpEarned: 0, xp: 0, level: 0, leveledUp: false,
};
const SANDBOX_MINE: MineResult = {
  oreDropped: 0, oreType: null, gemDropped: 0, drops: [], energy: 0, maxEnergy: 0,
  xpEarned: 0, xp: 0, level: 0, leveledUp: false,
};

export async function fellTree(c: FarmCtx, d: Decor, s: Sprite) {
  c.player?.stopAction();                              // stop the chop loop when it falls
  playSfx('tree_fall');                                // timber — fires with the fall, not the API round-trip
  const bx = s.x, by = s.y - s.height * 0.55;          // burst point (before it becomes a stump)
  try {
    // The tree's tile, so the logs drop where it stood.
    // Size decides the payout (1 small / 3 big), so it travels with the fell.
    const r = await dispatchAction('wood.chop', (id) => (c.sandbox
      ? Promise.resolve(SANDBOX_CHOP)
      : woodcuttingApi.chop(false, { tx: d.tx, ty: d.ty }, id, treeSizeOf(d.variant))));
    if (r.leveledUp) playSfx('level_up');
    c.felledUntil.set(d.id, Date.now() + (c.woodCfg?.regrowMs ?? FALLBACK_REGROW.tree));
    s.texture = stumpFor(c, d); s.scale.set(PX); s.tint = 0xffffff;   // stump matches the tree
    const sf = stumpFor(c, d).frame;
    s.hitArea = new Rectangle(-sf.width / 2, -sf.height, sf.width, sf.height);
    spawnFallingIcons(c, bx, by, 'wood', r.logsDropped);
    // No auto-pickup: the logs are now real ground drops (see r.drops); the drop
    // layer sweeps them up when the hero walks over them.
    c.onDrops?.(r.drops);
    toast(c.sandbox
      ? 'Timber! Nicely done — that is all there is to it.'
      : `Timber! 🪵 ${r.logsDropped} on the ground · stump left${r.leveledUp ? ' · LEVEL UP!' : ''}`);
    if (c.sandbox) return;   // nothing was spent or granted, so nothing to re-read
    await c.game.refreshHud();
      refreshBag(c);
  } catch (e) {
    toast(e instanceof ApiError ? e.message : 'Chop failed', 'error');
  }
}

// Grub out a leftover stump: a couple more logs, then it's gone until the tree regrows.
export async function clearStump(c: FarmCtx, d: Decor, s: Sprite) {
  c.player?.stopAction();
  playSfx('tree_fall');
  try {
    const r = await dispatchAction('wood.chop', (id) => (c.sandbox
      ? Promise.resolve(SANDBOX_CHOP)
      : woodcuttingApi.chop(true, { tx: d.tx, ty: d.ty }, id)));
    c.clearedStumps.add(d.id);
    // §4: uprooting frees the tile — walkable now, and tillable/buildable. Unblock
    // the collision cell live and bump the grid so walks/paths and the hoe re-eval.
    if (c.blockedGrid && c.world) {
      c.blockedGrid[d.ty * c.world.cols + d.tx] = 0;
      c.gridVersion++; c.pathCache.clear();
    }
    spawnFallingIcons(c, s.x, s.y - s.height * 0.5, 'wood', r.logsDropped);
    s.visible = false; s.eventMode = 'none'; s.tint = 0xffffff; s.cursor = 'default';
    c.onDrops?.(r.drops);
    toast(c.sandbox ? 'Stump cleared.' : `Stump cleared — 🪵 ${r.logsDropped} on the ground`);
    if (c.sandbox) return;
    await c.game.refreshHud();
      refreshBag(c);
  } catch (e) {
    toast(e instanceof ApiError ? e.message : 'Chop failed', 'error');
  }
}

// ─── Mining: break an ore rock → ore (+maybe gem) bursts out, auto-collected ───
export async function breakRock(c: FarmCtx, d: Decor, s: Sprite) {
  c.player?.stopAction();
  playSfx('rock_break');
  // The vein's METAL decides the gate (map/ores.ts). The client only NAMES the
  // ore — the server looks up the pickaxe requirement, the yield and the drop
  // from its own table, so this is a label, not a decision (Block 7 hard-gate).
  const ore = oreByVariant(d.variant);
  try {
    const r = await dispatchAction('mine.break', (id) => (c.sandbox
      ? Promise.resolve(SANDBOX_MINE)
      : miningApi.mine({ tx: d.tx, ty: d.ty }, ore.pickaxeTier, id, ore.type)));
    if (r.leveledUp) playSfx('level_up');
    c.minedUntil.set(d.id, Date.now() + (c.mineCfg?.regrowMs ?? FALLBACK_REGROW.ore));
    spawnFallingIcons(c, s.x, s.y - s.height * 0.5, 'ore', r.oreDropped);
    if (r.gemDropped) spawnFallingIcons(c, s.x, s.y - s.height * 0.5, 'gem', r.gemDropped);
    s.visible = false; s.eventMode = 'none'; s.tint = 0xffffff; s.cursor = 'default';
    c.onDrops?.(r.drops);
    toast(c.sandbox
      ? 'Cracked it! That is mining — the real veins pay out.'
      : `Cracked it! ${ore.name} ore ×${r.oreDropped}${r.gemDropped ? ` · 💎 ${r.gemDropped}` : ''} on the ground${r.leveledUp ? ' · LEVEL UP!' : ''}`);
    if (c.sandbox) return;
    await c.game.refreshHud();
      refreshBag(c);
  } catch (e) {
    // Below-tier pickaxe: sparks fly and the rock flashes red, but no progress —
    // the toast tells the player exactly which pickaxe they need.
    const msg = e instanceof ApiError ? e.message : 'Mining failed';
    if (msg.startsWith('Too hard')) {
      spawnChips(c, d, s);
      const prev = s.tint; s.tint = 0xf05555;
      setTimeout(() => { s.tint = prev; }, 200);
    }
    toast(msg, 'error');
  }
}

/**
 * Clear a grass tuft or wildflower by hand.
 *
 * Kept apart from `gatherProp` because ground cover is a different interaction:
 * no tool, no hold-to-gather ring, no walk-up ceremony — one tap, a quick pick
 * animation, and the tuft is gone. It regrows on the shortest timer in the game,
 * so tidying around a bed is cosmetic upkeep rather than a resource route.
 */
export async function gatherFoliage(c: FarmCtx, d: Decor, s: Sprite) {
  // One tap, no ring — but still a paid action, so it is priced before the swing
  // animation plays rather than after the server refuses.
  if (!affordEnergy(c, 'foliage')) return;
  const resource = gatherResource(d);
  playSfx('gather_plant');        // 'grass' | 'flowers'
  // Ground cover comes up with whatever is in hand — a hoe scythes it, an axe/pick
  // sweeps it aside, bare hands (or the knife) take the sword swing.
  if (c.player) void c.player.playAction(toolAction(c, 'attack'), dirTo(c.player.x, c.player.y, s.x, s.y));
  try {
    const r = await dispatchAction('gather', (id) => gatheringApi.gather(resource, { tx: d.tx, ty: d.ty }, id), { key: resource });
    c.gatheredUntil.set(d.id, Date.now() + (c.gatherCfg?.foliageRespawnMs ?? FALLBACK_REGROW.foliage));
    s.visible = false;
    spawnFallingIcons(c, s.x, s.y - s.height * 0.5, resource === 'grass' ? 'wood' : 'mushroom', r.earned,
      resource === 'grass' ? 'wood' : undefined);
    c.onDrops?.(r.drops);
    toast(resource === 'grass'
      ? `🌾 ${r.earned} straw${r.leveledUp ? ' · LEVEL UP!' : ''}`
      : `🌸 ${r.earned} petal${r.earned > 1 ? 's' : ''}${r.leveledUp ? ' · LEVEL UP!' : ''}`);
    await c.game.refreshHud();
      refreshBag(c);
  } catch (e) {
    // A failed clear must put the tuft back — it was only hidden optimistically.
    c.gatheredUntil.delete(d.id);
    s.visible = true;
    toast(e instanceof ApiError ? e.message : 'Could not clear that', 'error');
  }
}


/**
 * One mining pass at a big rock.
 *
 * A boulder is an outcrop, not a pebble: it takes BOULDER_PASSES rounds to break
 * up, and every round pays out stone, so the player is rewarded for the work as it
 * happens rather than only at the end. The sprite shrinks a little each pass so the
 * remaining work is legible without a health bar, and the final pass frees the tile
 * (the collision grid is rebuilt, since the boulder was a hard obstacle).
 */
export async function mineBoulderPass(c: FarmCtx, d: Decor, s: Sprite) {
  const landed = (c.boulderHits.get(d.id) ?? 0) + 1;
  const last = landed >= BOULDER_PASSES;
  try {
    // Each pass pays out as a normal stone gather — the server prices it, and the
    // tile is reported so the harvest survives a reload like every other node.
    // Only the LAST pass reports the tile. Every pass used to, so the server
    // wrote a full STONE_RESPAWN_MS harvest row on pass 1: reload halfway through
    // and the half-broken outcrop was hydrated away with its remaining stone,
    // while replaying the call farmed one boulder forever.
    const r = await dispatchAction('gather', (id) => gatheringApi.gather('stone', last ? { tx: d.tx, ty: d.ty } : undefined, id), { key: 'stone' });
    c.boulderHits.set(d.id, landed);
    spawnFallingIcons(c, s.x, s.y - s.height * 0.5, 'stone', r.earned);
    c.onDrops?.(r.drops);
    if (last) {
      playSfx('rock_break');
      c.gatheredUntil.set(d.id, Date.now() + (c.gatherCfg?.stoneRespawnMs ?? FALLBACK_REGROW.stone));
      c.boulderHits.delete(d.id);
      s.visible = false; s.eventMode = 'none'; s.cursor = 'default';
      buildCollision(c);                 // the outcrop is gone — the tile opens up
      toast(`🪨 The rock breaks apart · ${r.earned} stone${r.leveledUp ? ' · LEVEL UP!' : ''}`);
    } else {
      // Chip it down: each surviving pass leaves a visibly smaller rock.
      s.scale.set(s.scale.x * 0.88, s.scale.y * 0.88);
      toast(`🪨 ${r.earned} stone · ${BOULDER_PASSES - landed} more to break it`);
    }
    await c.game.refreshHud();
      refreshBag(c);
  } catch (e) {
    toast(e instanceof ApiError ? e.message : 'Mining failed', 'error');
  }
}

// ─── Gathering: pick up a small rock / mushroom → resource bursts out ─────────
export async function gatherProp(c: FarmCtx, d: Decor, s: Sprite) {
  const resource = gatherResource(d);
  // A stone is prised out with the pick; a bush/mushroom is picked by hand.
  playSfx(resource === 'stone' ? 'rock_break' : 'gather_plant');
  try {
    const r = await dispatchAction('gather', (id) => gatheringApi.gather(resource, { tx: d.tx, ty: d.ty }, id), { key: resource });
    const respawn = resource === 'stone' ? (c.gatherCfg?.stoneRespawnMs ?? FALLBACK_REGROW.stone)
      // A hauled-away woodpile does not come back — the server clears the tile for
      // good, same as stone, so the client must not pencil in a regrow either.
      : resource === 'logs' ? (c.gatherCfg?.logsRespawnMs ?? c.gatherCfg?.stoneRespawnMs ?? FALLBACK_REGROW.stone)
      : resource === 'bush' ? (c.gatherCfg?.bushRespawnMs ?? FALLBACK_REGROW.bush)
      : (c.gatherCfg?.mushroomRespawnMs ?? FALLBACK_REGROW.mushroom);
    c.gatheredUntil.set(d.id, Date.now() + respawn);
    // A bush drops sticks (wood); reuse the wood icon burst. Ground cover never
    // reaches here — it has its own one-tap path (gatherFoliage).
    const icon = resource === 'bush' || resource === 'grass' || resource === 'logs' ? 'wood'
      : resource === 'stone' ? 'stone' : 'mushroom';
    spawnFallingIcons(c, s.x, s.y - s.height * 0.5, icon, r.earned, resource === 'bush' || resource === 'logs' ? 'wood' : undefined);
    s.visible = false; s.eventMode = 'none'; s.tint = 0xffffff; s.cursor = 'default';
    c.onDrops?.(r.drops);
    toast(resource === 'stone'
      ? `🪨 ${r.earned} stone on the ground${r.leveledUp ? ' · LEVEL UP!' : ''}`
      : resource === 'logs'
      ? `🪵 ${r.earned} logs hauled away${r.leveledUp ? ' · LEVEL UP!' : ''}`
      : resource === 'bush'
      ? `🌿 ${r.earned} stick${r.earned > 1 ? 's' : ''} on the ground${r.leveledUp ? ' · LEVEL UP!' : ''}`
      : `🍄 ${r.earned} mushroom${r.earned > 1 ? 's' : ''} on the ground${r.leveledUp ? ' · LEVEL UP!' : ''}`);
    await c.game.refreshHud();
      refreshBag(c);
  } catch (e) {
    toast(e instanceof ApiError ? e.message : 'Gather failed', 'error');
  }
}

// ─── Acorns: plant a tree ────────────────────────────────────────────────────
/**
 * Push one acorn into the grass at a tile.
 *
 * The tile rules were already answered by plantBlocker before we got here (the
 * client owns the map; the server owns the acorn, the energy and the clock), so
 * this only commits and then makes the new tree REAL on the map immediately:
 * pushing the decor + its ripeness by hand rather than waiting for the next world
 * rebuild, which only happens when maxPlots changes — otherwise the acorn vanished
 * until a reload and read as a swallowed action.
 */
export async function plantAcornAt(c: FarmCtx, tx: number, ty: number) {
  try {
    const r = await dispatchAction('wood.plant-acorn',
      (id) => woodcuttingApi.plantAcorn(tx, ty, id), { key: `${tx},${ty}` });
    playSfx('build_place');
    const world = c.world;
    if (world) {
      const id = saplingDecorId(tx, ty);
      if (!world.decor.some((d) => d.id === id)) {
        const d: Decor = { id, kind: 'tree', variant: SAPLING_VARIANT, tx, ty, ox: 0, oy: 0 };
        world.decor.push(d);
        const k = bkey(tx, ty);
        const list = c.tallBuckets.get(k);
        if (list) list.push(d); else c.tallBuckets.set(k, [d]);
      }
      (world.saplings ??= new Map()).set(id, Date.parse(r.sapling.readyAt));
      // The snapshot the next repaint hydrates from must agree with what is on the
      // map right now, or a farm refresh between now and the next world rebuild
      // would rebuild the scene without the tree that is standing there.
      if (c.farm) c.farm.saplings = [...(c.farm.saplings ?? []), r.sapling];
      // A tree is an obstacle: rebuild the grid so nobody walks through the trunk.
      buildCollision(c);
    }
    toast(`Acorn planted 🌰 — a tree in ${fmtWait(r.sapling.remainingMs)}`);
    await c.game.refreshHud();
    refreshBag(c);
  } catch (e) {
    if (isDropped(e)) return;                    // the funnel refused to send — the first tap is landing
    toast(e instanceof ApiError ? e.message : 'Planting failed', 'error');
  }
}
